/**
 * FR-017 — one place that knows which queries read the link table.
 *
 * A link is read from three directions: the entity's Belege
 * (`['twin','entities',type,id,'documents'|'documents-bulk',…]`, `useTwin.ts`),
 * a document's "Verknüpfte Objekte" (`['twin','document-links',docId]`) and an
 * entity's own link rows (`['twin','target-links',type,id]`). A write from any
 * one of them must refresh all three, or the other surfaces keep showing a
 * link the farmer just removed until their staleTime runs out — silently.
 * The Belege keys are matched by predicate, not a guessed prefix: the first
 * version invalidated a key no query uses (FR-017 #3), and `isBelegeQuery` is
 * pinned against the literal keys in `useTwin.ts` for that reason.
 */
import type { QueryClient } from '@tanstack/react-query';

export const documentLinksKey = (documentId: string) => ['twin', 'document-links', documentId] as const;
export const targetLinksKey = (targetType: string, targetId: string) =>
  ['twin', 'target-links', targetType, targetId] as const;

/** Any query that reads the entity → documents direction (`useTwin.ts`). */
export function isBelegeQuery(query: { queryKey: readonly unknown[] }): boolean {
  const [scope, family] = query.queryKey;
  if (scope !== 'twin' || family !== 'entities') return false;
  return query.queryKey.includes('documents') || query.queryKey.includes('documents-bulk');
}

/** FR-017 #7a — the tenant's link census (the "Ohne Verknüpfung" count). */
export const linkCensusKey = ['twin', 'link-census'] as const;

export function refreshLinkQueries(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ['twin', 'document-links'] });
  void queryClient.invalidateQueries({ queryKey: linkCensusKey });
  void queryClient.invalidateQueries({ queryKey: ['twin', 'target-links'] });
  void queryClient.invalidateQueries({ predicate: isBelegeQuery });
}
